import { FC, PropsWithChildren, useEffect } from 'react';

import { getStateValueStrings } from '@eneco/common/xstate/stateValue';
import { useTranslation } from '@eneco/i18n';
import { Route, RouterProvider, Routes, useRouter } from '@eneco/next';
import { Skeleton } from '@eneco/sparky';

import { ConfirmationStep } from '../ConfirmationStep/ConfirmationStep';
import { ErrorDefault } from '../EndStep/pages/error/ErrorDefault/ErrorDefault';
import { Adhoc } from '../EndStep/pages/thankyou/Adhoc/Adhoc';
import { CloseDown } from '../EndStep/pages/thankyou/CloseDown/CloseDown';
import { EnergyReport } from '../EndStep/pages/thankyou/EnergyReport/EnergyReport';
import { PeriodicDelayed } from '../EndStep/pages/thankyou/PeriodicDelayed/PeriodicDelayed';
import { PeriodicEven } from '../EndStep/pages/thankyou/PeriodicEven/PeriodicEven';
import { PeriodicGetBack } from '../EndStep/pages/thankyou/PeriodicGetBack/PeriodicGetBack';
import { PeriodicGetBackTermAmount } from '../EndStep/pages/thankyou/PeriodicGetBackTermAmount/PeriodicGetBackTermAmount';
import { PeriodicNewTermAmount } from '../EndStep/pages/thankyou/PeriodicNewTermAmount/PeriodicNewTermAmount';
import { PeriodicPayBack } from '../EndStep/pages/thankyou/PeriodicPayBack/PeriodicPayBack';
import { PeriodicPayBackLess } from '../EndStep/pages/thankyou/PeriodicPayBackLess/PeriodicPayBackLess';
import { PeriodicPayBackMore } from '../EndStep/pages/thankyou/PeriodicPayBackMore/PeriodicPayBackMore';
import { PeriodicPayBackTermAmount } from '../EndStep/pages/thankyou/PeriodicPayBackTermAmount/PeriodicPayBackTermAmount';
import { Registration } from '../EndStep/pages/thankyou/Registration/Registration';
import { RelocationNewAddress } from '../EndStep/pages/thankyou/RelocationNewAddress/RelocationNewAddress';
import { RelocationOldAddress } from '../EndStep/pages/thankyou/RelocationOldAddress/RelocationOldAddress';
import { Tme } from '../EndStep/pages/thankyou/Tme/Tme';
import { IntroductionStep } from '../IntroductionStep/IntroductionStep';
import { MeterReadingStep } from '../MeterReadingStep/MeterReadingStep';
import { ReadingIdStep } from '../ReadingIdStep/ReadingIdStep';
import { ReadingIntake } from '../ReadingIntake/ReadingIntake';
import { useMeterReadingsFlow } from '../state/MeterReadingsFlow.machine';

export const MeterReadingsRoutes: Record<string, string> = {
  READING_INTAKE: '/',
  READING_ID: '/opnamekenmerk/',
  'INTRODUCTION.ENDNOTE': '/eindnota/',
  'INTRODUCTION.YEARNOTE': '/jaarnota/',
  'INTRODUCTION.TME': '/tariefwijziging/',
  'INTRODUCTION.ENERGY_REPORT': '/energierapport/',
  'METER_READINGS.ELECTRICITY': '/stroom/',
  'METER_READINGS.GAS': '/gas/',
  'METER_READINGS.WARMTH': '/warmte/',
  'METER_READINGS.COOLING': '/koeling/',
  'METER_READINGS.TAPWATER': '/warmwater/',
  CONFIRMATION: '/bevestigen/',
  'THANK_YOU.Adhoc': '/bedankt/',
  'THANK_YOU.TariffMigration': '/bedankt/tariefwissel/',
  'THANK_YOU.PeriodicEven': '/bedankt/jaarnota-we-staan-quitte/',
  'THANK_YOU.PeriodicNewTermAmount': '/bedankt/jaarnota-nieuw-termijnbedrag/',
  'THANK_YOU.PeriodicPayBack': '/bedankt/jaarnota-bijbetalen/',
  'THANK_YOU.PeriodicPayBackMore': '/bedankt/jaarnota-bijbetalen-meer/',
  'THANK_YOU.PeriodicPayBackLess': '/bedankt/jaarnota-bijbetalen-minder/',
  'THANK_YOU.PeriodicPayBackTermAmount': '/bedankt/jaarnota-termijnbedrag-bijbetalen/',
  'THANK_YOU.PeriodicGetBackTermAmount': '/bedankt/jaarnota-termijnbedrag-geld-terug/',
  'THANK_YOU.PeriodicGetBack': '/bedankt/jaarnota-geld-terug/',
  'THANK_YOU.PeriodicDelayed': '/bedankt/jaarnota-volgt/',
  'THANK_YOU.CloseDown': '/bedankt/opzegging/',
  'THANK_YOU.Registration': '/bedankt/aanmelding/',
  'THANK_YOU.RelocationNewAddress': '/bedankt/nieuwe-woning/',
  'THANK_YOU.RelocationOldAddress': '/bedankt/oude-woning/',
  'THANK_YOU.EnergyReport': '/bedankt/energierapport/',
  'THANK_YOU.Unknown': '/bedankt-onbekend/',
  ERROR_PAGE: '/foutmelding/',
};

export const StateRouter: FC<PropsWithChildren> = ({ children }) => {
  return <StateRouterComponent>{children}</StateRouterComponent>;
};

const StateRouterComponent: FC<PropsWithChildren> = ({ children }) => {
  const { activeRoute, push } = useRouter();
  const { state } = useMeterReadingsFlow();
  const { t, isLoading } = useTranslation();

  const stateValues = getStateValueStrings(state.value);

  const [, stateRoute] =
    Object.entries(MeterReadingsRoutes).find(([meterReadingsState]) => stateValues.includes(meterReadingsState)) || [];

  const isCurrentStateRoute = activeRoute === stateRoute;

  useEffect(() => {
    if (stateRoute && !isCurrentStateRoute) push(stateRoute);
  }, [t, isCurrentStateRoute, push, state, isLoading, stateRoute]);

  if (!isCurrentStateRoute) return <Skeleton height={400} />;

  return children;
};

export const MeterReadingsRouter: FC = () => (
  <RouterProvider>
    <StateRouter>
      <Routes>
        <Route route={MeterReadingsRoutes.READING_INTAKE} component={ReadingIntake} />
        <Route route={MeterReadingsRoutes.READING_ID} component={ReadingIdStep} />
        <Route route={MeterReadingsRoutes['INTRODUCTION.ENDNOTE']} component={IntroductionStep} />
        <Route route={MeterReadingsRoutes['INTRODUCTION.YEARNOTE']} component={IntroductionStep} />
        <Route route={MeterReadingsRoutes['INTRODUCTION.TME']} component={IntroductionStep} />
        <Route route={MeterReadingsRoutes['INTRODUCTION.ENERGY_REPORT']} component={IntroductionStep} />
        <Route route={MeterReadingsRoutes['METER_READINGS.ELECTRICITY']} component={MeterReadingStep} />
        <Route route={MeterReadingsRoutes['METER_READINGS.GAS']} component={MeterReadingStep} />
        <Route route={MeterReadingsRoutes['METER_READINGS.WARMTH']} component={MeterReadingStep} />
        <Route route={MeterReadingsRoutes['METER_READINGS.COOLING']} component={MeterReadingStep} />
        <Route route={MeterReadingsRoutes['METER_READINGS.TAPWATER']} component={MeterReadingStep} />
        <Route route={MeterReadingsRoutes.CONFIRMATION} component={ConfirmationStep} />
        <Route route={MeterReadingsRoutes['THANK_YOU.Adhoc']} component={Adhoc} />
        <Route route={MeterReadingsRoutes['THANK_YOU.TariffMigration']} component={Tme} />
        <Route route={MeterReadingsRoutes['THANK_YOU.PeriodicEven']} component={PeriodicEven} />
        <Route route={MeterReadingsRoutes['THANK_YOU.PeriodicNewTermAmount']} component={PeriodicNewTermAmount} />
        <Route route={MeterReadingsRoutes['THANK_YOU.PeriodicPayBack']} component={PeriodicPayBack} />
        <Route route={MeterReadingsRoutes['THANK_YOU.PeriodicPayBackMore']} component={PeriodicPayBackMore} />
        <Route route={MeterReadingsRoutes['THANK_YOU.PeriodicPayBackLess']} component={PeriodicPayBackLess} />
        <Route route={MeterReadingsRoutes['THANK_YOU.Unknown']} component={Adhoc} />
        <Route
          route={MeterReadingsRoutes['THANK_YOU.PeriodicPayBackTermAmount']}
          component={PeriodicPayBackTermAmount}
        />
        <Route
          route={MeterReadingsRoutes['THANK_YOU.PeriodicGetBackTermAmount']}
          component={PeriodicGetBackTermAmount}
        />
        <Route route={MeterReadingsRoutes['THANK_YOU.PeriodicGetBack']} component={PeriodicGetBack} />
        <Route route={MeterReadingsRoutes['THANK_YOU.PeriodicDelayed']} component={PeriodicDelayed} />
        <Route route={MeterReadingsRoutes['THANK_YOU.CloseDown']} component={CloseDown} />
        <Route route={MeterReadingsRoutes['THANK_YOU.Registration']} component={Registration} />
        <Route route={MeterReadingsRoutes['THANK_YOU.RelocationNewAddress']} component={RelocationNewAddress} />
        <Route route={MeterReadingsRoutes['THANK_YOU.RelocationOldAddress']} component={RelocationOldAddress} />
        <Route route={MeterReadingsRoutes['THANK_YOU.EnergyReport']} component={EnergyReport} />
        <Route route={MeterReadingsRoutes['ERROR_PAGE']} component={ErrorDefault} />
      </Routes>
    </StateRouter>
  </RouterProvider>
);
